import React, { useEffect, useState } from 'react';
import {
    Box,
    TextField,
    Typography,
    Divider,
    IconButton,
    Grid,
    Modal,
    Card,
    CircularProgress,
} from '@mui/material';
import {
    faFaceSmile,
    faFaceFrown,
    faHeart,
    faPaperPlane,
    faThumbsDown,
    faThumbsUp,
    IconName,
} from '@fortawesome/sharp-regular-svg-icons';

import { faPaperclip, faPaperclipVertical, faTimes } from '@fortawesome/sharp-solid-svg-icons';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { UserContact } from '../../molecules/UserContact';
import { Scrollbar } from '../../molecules';
import { MessageLine, type MessageLineProps } from './MessageLine';
import { UnreadSeparator } from './UnreadSeparator';

export type BandeauContact = {
    userIdentifier: string;
    role: string;
    mail?: string;
    phone?: string;
    badge?: string | undefined;
};

export type MessageItemDetailData = {
    type: 'message';
    message: string;
};

export type DocumentItemDetailData = {
    type: 'document';
    dosDocFileId: number;
    dosDocFileName: string;
    dosDocFileUrl: string;
};

export type IconItemDetailData = {
    type: 'emoji';
    icon: string;
};

export type WebSocketData = MessageItemDetailData | DocumentItemDetailData | IconItemDetailData;

export type MessageItem = {
    messageId: string;
    data: {
        topicType?: string;
        discriminant: {
            [x: string]: unknown;
        };
        userIdentifier: string;
        topicId: string;
        messageId: string;
        date: string;
        reader: {
            client: 'true' | 'false';
            interne: 'true' | 'false';
            [x: string]: unknown;
        };
        message: WebSocketData;
    };
};

export type MessageBoxProps = {
    destinataire?: BandeauContact;
    messages: MessageItem[];
    userIdentifier: string;
    isLoading?: boolean;
    isError?: boolean;
    onSend: (
        messageData: MessageItemDetailData | DocumentItemDetailData | IconItemDetailData,
    ) => void;
    onEdit: (messageId: string, messageData: MessageItemDetailData) => void;
    onDelete: (messageId: string) => void;
    onUploadFile?: (file: File) => void;
    onScrollTop?: () => void;
    onMessageDocumentClick: (fileName: string, id: number) => void;
    userMapping?: Record<string, MessageLineProps['user']>;
    anonymous: boolean;
    isFormaliste: boolean;
};

export const MessageBox: React.FunctionComponent<MessageBoxProps> = function ({
    destinataire,
    messages,
    userIdentifier,
    isLoading,
    isError,
    onSend,
    onEdit,
    onDelete,
    onUploadFile,
    onScrollTop,
    onMessageDocumentClick,
    userMapping = {},
    anonymous,
    isFormaliste,
}) {
    const [input, setInput] = useState('');
    const [fileUpload, setFileUpload] = useState<File | null>(null);
    const [emojiModalOpen, setEmojiModalOpen] = useState(false);
    const emojiList: Array<{ icon: IconName; label: string }> = [
        { icon: faHeart.iconName, label: 'Heart' },
        { icon: faFaceSmile.iconName, label: 'Face Smile' },
        { icon: faFaceFrown.iconName, label: 'Face Frown' },
        { icon: faThumbsUp.iconName, label: 'Thumbs Up' },
        { icon: faThumbsDown.iconName, label: 'Thumbs Down' },
    ];

    const handleSendText = () => {
        if (input.trim()) {
            onSend({ type: 'message', message: input.trim() });
            setInput('');
        }
    };

    const handleSendEmoji = (icon: IconName) => {
        onSend({ type: 'emoji', icon });
        setEmojiModalOpen(false);
    };

    const handleSendFile = () => {
        if (onUploadFile && fileUpload) {
            onUploadFile(fileUpload);
        }
        setFileUpload(null);
    };

    const handleAttachFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFileUpload(e.target.files[0]);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if ((e.metaKey || e.ctrlKey) && (e.code === 'Enter' || e.code === 'NumpadEnter')) {
            handleSendText();
        }
    };

    // Effect to scroll to the bottom of chat messages
    useEffect(() => {
        const messageEndFormElement = document.getElementById('messages_end');
        const unreadFormElement = document.getElementById('first_unread');
        if (messageEndFormElement) {
            messageEndFormElement.scrollIntoView();
        }
        if (unreadFormElement) {
            unreadFormElement.scrollIntoView();
        }
    }, [messages]);

    let userDestinataire =
        destinataire && destinataire.userIdentifier
            ? userMapping[destinataire.userIdentifier]
            : null;
    if (anonymous && userDestinataire && userDestinataire.idUserGie) {
        userDestinataire = { firstName: 'Equipe', lastName: 'Legal2Digital', idUserGie: 9999 };
    }

    const firstUnread: number = messages.findIndex((msg) =>
        isFormaliste
            ? String(msg.data.reader.interne) !== 'true'
            : String(msg.data.reader.client) !== 'true',
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}
        >
            <Card
                elevation={3}
                sx={{
                    maxHeight: 600,
                    minHeight: 400,
                    overflow: 'auto',
                    py: 2,
                    px: 0,
                    mx: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflowX: 'hidden',
                    overflowY: 'hidden',
                }}
            >
                {/* BANDEAU DESTINATAIRE / CONTACT */}
                {destinataire && (
                    <>
                        <Box sx={{ px: 2 }}>
                            <UserContact
                                user={userDestinataire}
                                userIdentifier={!anonymous ? destinataire.userIdentifier : null}
                                role={destinataire.role}
                                mail={destinataire.mail}
                                phone={destinataire.phone}
                                badge={destinataire?.badge}
                            />
                        </Box>
                        <Divider sx={{ my: 1 }} />
                    </>
                )}
                {/* LISTE MESSAGE */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        px: 2,
                    }}
                >
                    {isLoading && !isError && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    <Scrollbar
                        style={{
                            maxHeight: `400px`,
                        }}
                        onReachTop={onScrollTop}
                    >
                        {messages.map((msg, index) => {
                            const user = userMapping[msg.data.userIdentifier];
                            return (
                                <>
                                    {index === firstUnread && firstUnread !== -1 && (
                                        <UnreadSeparator id="first_unread" />
                                    )}
                                    <MessageLine
                                        key={msg.messageId}
                                        message={msg}
                                        previousAuthor={messages[index - 1]?.data.userIdentifier}
                                        userIdentifier={userIdentifier}
                                        user={user}
                                        anonymous={anonymous}
                                        onClick={onMessageDocumentClick}
                                        onEdit={onEdit}
                                        onDelete={onDelete}
                                    />
                                </>
                            );
                        })}
                        <div id="messages_end" />
                    </Scrollbar>
                </Box>
                <Divider sx={{ my: 2 }} />
                {/* FORMULAIRE ENVOI MESSAGE */}
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    padding={2}
                >
                    <input
                        type="file"
                        id="file-upload"
                        hidden
                        onChange={handleAttachFile}
                    />
                    <label htmlFor="file-upload">
                        <IconButton component="span">
                            <FaOptionIcon
                                icon={faPaperclipVertical}
                                size="sm"
                                color="text.gris50"
                            />
                        </IconButton>
                    </label>
                    <IconButton
                        onClick={() => setEmojiModalOpen(true)}
                        disabled={!!fileUpload}
                    >
                        <FaOptionIcon
                            icon={faFaceSmile}
                            size="sm"
                            color="text.gris50"
                        />
                    </IconButton>
                    {fileUpload ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: 2,
                                px: 2,
                                py: 1,
                                flexGrow: 1,
                                gap: 1,
                            }}
                        >
                            <FaOptionIcon
                                icon={faPaperclip}
                                size="sm"
                                color="text.gris50"
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    flexGrow: 1,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                Pièce jointe : {fileUpload.name}
                                <IconButton
                                    onClick={() => setFileUpload(null)}
                                    size="small"
                                >
                                    <FaOptionIcon
                                        icon={faTimes}
                                        size="sm"
                                        color="text.gris50"
                                    />
                                </IconButton>
                            </Typography>
                        </Box>
                    ) : (
                        <TextField
                            variant="standard"
                            fullWidth
                            placeholder="Écrire un message..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => handleKeyPress(e)}
                            InputProps={{ disableUnderline: true }}
                            disabled={!!fileUpload}
                            multiline
                        />
                    )}
                    <IconButton
                        color="primary"
                        onClick={fileUpload ? handleSendFile : handleSendText}
                    >
                        <FaOptionIcon
                            icon={faPaperPlane}
                            size="sm"
                            color="text.gris50"
                        />
                    </IconButton>
                </Box>
            </Card>
            {/* MODAL CHOIX / ENVOI EMOJI */}
            <Modal
                open={emojiModalOpen}
                onClose={() => setEmojiModalOpen(false)}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                        minWidth: 300,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'start',
                            justifyContent: 'start',
                            gap: 2,
                            mr: 4,
                            mb: 2,
                        }}
                    >
                        <Typography
                            variant="h4"
                            gutterBottom
                        >
                            Choisissez un emoji :
                        </Typography>
                        <Grid
                            container
                            spacing={2}
                        >
                            {emojiList.map(({ icon, label }) => (
                                <Grid
                                    item
                                    key={`${icon}-${label}`}
                                >
                                    <IconButton onClick={() => handleSendEmoji(icon)}>
                                        <FaOptionIcon
                                            icon={icon}
                                            size="2x"
                                        />
                                    </IconButton>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};
